import { useCallback, useEffect, useRef, useState } from "react";
import { UploadServices } from "../../services/UploadService";
import { Request } from "../../services/Request";
import { urls } from "../../Utils/Urls";
import { TENANT_ID, getCurrentLanguage } from "../../components/Utils";

// Same limits as dfs-mobile-app VoiceRecordingController.
export const VOICE_MAX_MS = 15000;
export const VOICE_SILENCE_MS = 2000;
const VOICE_THRESHOLD = 0.3;
const VOICE_FILESTORE_MODULE = "GlobalSearch";
const TICK_MS = 100;
// Chrome's audio/mp4 is Opus in an isom box, which filestore rejects as video/quicktime; mp4 stays for Safari.
const FORMATS = [
  { mimeType: "audio/webm;codecs=opus", ext: "webm" },
  { mimeType: "audio/webm", ext: "webm" },
  { mimeType: "audio/mp4", ext: "m4a" },
];

export const VoiceState = Object.freeze({ IDLE: "idle", RECORDING: "recording", PROCESSING: "processing" });
export const VoiceError = Object.freeze({
  NO_SPEECH: "noSpeech",
  PERMISSION: "permission",
  NO_DEVICE: "noDevice",
  BUSY: "busy",
  FAILED: "failed",
});

// getUserMedia rejects without a prompt when the site is blocked, no mic exists or it is in use.
const MEDIA_ERRORS = {
  NotAllowedError: VoiceError.PERMISSION,
  SecurityError: VoiceError.PERMISSION,
  NotFoundError: VoiceError.NO_DEVICE,
  OverconstrainedError: VoiceError.NO_DEVICE,
  NotReadableError: VoiceError.BUSY,
  AbortError: VoiceError.BUSY,
};

const pickFormat = () =>
  typeof MediaRecorder === "undefined"
    ? null
    : FORMATS.find(({ mimeType }) => MediaRecorder.isTypeSupported?.(mimeType)) ?? null;

export const isVoiceInputSupported = () =>
  Boolean(typeof navigator !== "undefined" && navigator.mediaDevices?.getUserMedia && pickFormat());

// Mobile normalises dBFS as (db + 45) / 45.
const levelOf = (analyser, buffer) => {
  analyser.getFloatTimeDomainData(buffer);
  let sum = 0;
  for (const sample of buffer) sum += sample * sample;
  const rms = Math.sqrt(sum / buffer.length);
  if (rms === 0) return 0;
  return Math.min(Math.max((20 * Math.log10(rms) + 45) / 45, 0), 1);
};

const readUserUuid = () => {
  try {
    return JSON.parse(localStorage.getItem("DfsWeb.user-info") ?? "null")?.uuid;
  } catch {
    return undefined;
  }
};

const transcribe = async (blob, ext) => {
  // Filestore matches the part Content-Type exactly, so codec parameters must go.
  const file = new File([blob], `voice_recording_${Date.now()}.${ext}`, { type: blob.type.split(";")[0].trim() });
  const upload = await UploadServices.Filestorage(VOICE_FILESTORE_MODULE, [file], TENANT_ID);
  const fileStoreId = upload?.data?.files?.[0]?.fileStoreId;
  if (!fileStoreId) throw new Error("voice upload returned no fileStoreId");

  const response = await Request({
    url: urls.speech_to_text,
    data: {
      RequestInfo: { apiId: "dfs-personalization", userInfo: { tenantId: TENANT_ID, uuid: readUserUuid() } },
      SpeechToTextCriteria: { fileStoreId, locale: getCurrentLanguage() === "hi_IN" ? "hi" : "en" },
    },
  });
  if (!(response?.status >= 200 && response.status < 300)) throw new Error("speech to text failed");
  const data = response.data ?? {};
  return String(data.transcribedText ?? data.text ?? data.transcription ?? "");
};

export default function useVoiceToText({ onTranscript }) {
  const [state, setState] = useState(VoiceState.IDLE);
  const [error, setError] = useState(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const sessionRef = useRef(null);
  const mountedRef = useRef(true);
  const onTranscriptRef = useRef(onTranscript);
  onTranscriptRef.current = onTranscript;

  const release = useCallback((session) => {
    clearInterval(session.tick);
    clearTimeout(session.silence);
    clearTimeout(session.cap);
    session.stream.getTracks().forEach((track) => track.stop());
    session.audioContext?.close().catch(() => {});
    if (sessionRef.current === session) sessionRef.current = null;
  }, []);

  const finish = useCallback(
    (send) => {
      const session = sessionRef.current;
      if (!session) return;
      session.send = send;
      release(session);
      if (session.recorder.state !== "inactive") session.recorder.stop();
    },
    [release]
  );

  const start = useCallback(async () => {
    if (sessionRef.current) return;
    setError(null);
    const format = pickFormat();
    if (!format) {
      setError(VoiceError.FAILED);
      return;
    }

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, noiseSuppression: true, autoGainControl: true, echoCancellation: true },
      });
    } catch (mediaError) {
      console.warn("Voice input: getUserMedia failed", mediaError?.name, mediaError?.message);
      if (mountedRef.current) setError(MEDIA_ERRORS[mediaError?.name] ?? VoiceError.FAILED);
      return;
    }
    if (!mountedRef.current) {
      stream.getTracks().forEach((track) => track.stop());
      return;
    }

    const recorder = new MediaRecorder(stream, { mimeType: format.mimeType, audioBitsPerSecond: 128000 });
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    const audioContext = AudioCtx ? new AudioCtx() : null;
    const analyser = audioContext?.createAnalyser() ?? null;
    if (analyser) audioContext.createMediaStreamSource(stream).connect(analyser);
    const buffer = analyser ? new Float32Array(analyser.fftSize) : null;

    const chunks = [];
    const session = { recorder, stream, audioContext, send: true, voiceDetected: false, startedAt: Date.now() };
    sessionRef.current = session;

    recorder.ondataavailable = (event) => {
      if (event.data?.size) chunks.push(event.data);
    };
    recorder.onstop = async () => {
      if (!session.send || !mountedRef.current) return;
      setState(VoiceState.PROCESSING);
      try {
        const text = (await transcribe(new Blob(chunks, { type: recorder.mimeType || format.mimeType }), format.ext)).trim();
        if (!mountedRef.current) return;
        if (text) onTranscriptRef.current?.(text);
        else setError(VoiceError.NO_SPEECH);
      } catch (transcribeError) {
        console.warn("Voice input: transcription failed", transcribeError?.response?.data?.Errors?.[0]?.code ?? transcribeError?.message);
        if (mountedRef.current) setError(VoiceError.FAILED);
      } finally {
        if (mountedRef.current) setState(VoiceState.IDLE);
      }
    };

    // Silence only ends the take once speech has been heard, as on mobile.
    session.tick = setInterval(() => {
      setElapsedMs(Date.now() - session.startedAt);
      if (!analyser) return;
      if (levelOf(analyser, buffer) > VOICE_THRESHOLD) {
        session.voiceDetected = true;
        clearTimeout(session.silence);
        session.silence = null;
      } else if (session.voiceDetected && !session.silence) {
        session.silence = setTimeout(() => finish(true), VOICE_SILENCE_MS);
      }
    }, TICK_MS);
    session.cap = setTimeout(() => finish(true), VOICE_MAX_MS);

    recorder.start();
    setElapsedMs(0);
    setState(VoiceState.RECORDING);
  }, [finish]);

  const stop = useCallback(() => finish(true), [finish]);

  const reset = useCallback(async () => {
    if (!sessionRef.current) return;
    finish(false);
    await start();
  }, [finish, start]);

  const cancel = useCallback(() => {
    finish(false);
    setState(VoiceState.IDLE);
  }, [finish]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      finish(false);
    };
  }, [finish]);

  return {
    supported: isVoiceInputSupported(),
    state,
    error,
    elapsedMs,
    progress: Math.min(elapsedMs / VOICE_MAX_MS, 1),
    start,
    stop,
    reset,
    cancel,
    clearError: useCallback(() => setError(null), []),
  };
}
